<template>
  <div class="flex flex-col h-full" :class="isMobile ? 'bg-gray-50 dark:bg-[#0d1117]' : ''">
    <!-- Desktop: search (left) + filter chips (right), one row -->
    <div v-if="!isMobile" class="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
      <div class="relative flex-shrink-0 w-64">
        <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search tasks..."
          class="w-full pl-9 pr-3 py-1 text-sm bg-gray-50 dark:bg-gray-800 border border-transparent rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-[#1C2128] transition-colors"
        />
      </div>

      <div class="flex items-center gap-1.5 overflow-x-auto ml-auto">
        <button
          v-for="chip in CHIPS"
          :key="chip.value"
          @click="store.fetchChecklist(chip.value)"
          class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors"
          :class="store.checklistScope === chip.value
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'"
        >
          {{ chip.label }}
          <span v-if="chipCount(chip.value)" class="tabular-nums" :class="countClass(chip.value)">{{ chipCount(chip.value) }}</span>
        </button>
      </div>
    </div>

    <!-- Mobile: full-width search, scope pills scroll below — native
         segmented-filter feel (Reminders/Things-style), not a cramped
         shared row with the desktop layout. -->
    <div v-else class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700 pb-2.5">
      <div class="px-4 pt-2.5">
        <div class="relative">
          <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search tasks..."
            class="w-full pl-10 pr-3 py-2.5 text-[15px] bg-gray-100 dark:bg-gray-800 border border-transparent rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-[#0d1117] transition-colors"
          />
        </div>
      </div>
      <div class="flex items-center gap-1.5 px-4 pt-2.5 overflow-x-auto no-scrollbar">
        <button
          v-for="chip in CHIPS"
          :key="chip.value"
          @click="store.fetchChecklist(chip.value)"
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-semibold rounded-full whitespace-nowrap transition-colors"
          :class="store.checklistScope === chip.value
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 active:bg-gray-200 dark:active:bg-gray-700'"
        >
          {{ chip.label }}
          <span v-if="chipCount(chip.value)" class="tabular-nums" :class="countClass(chip.value)">{{ chipCount(chip.value) }}</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="store.checklistLoading && !store.checklistItems.length" class="animate-pulse">
      <template v-if="!isMobile">
        <div class="divide-y divide-gray-100 dark:divide-gray-800">
          <div v-for="i in skeletonCount" :key="i" class="flex items-center gap-3 px-4 py-2.5">
            <div class="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3 flex-1 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded mx-4 mt-4 mb-2"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div v-for="i in skeletonCount" :key="i" class="flex items-center gap-3 px-4 py-3.5">
            <div class="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3 flex-1 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </template>
    </div>

    <!-- Error -->
    <div v-else-if="store.checklistError" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.checklistError }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Try again</button>
    </div>

    <!-- Empty (but not while the inline add row is open) -->
    <div v-else-if="!store.checklistItems.length && !isAdding" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <p class="text-sm text-gray-400 dark:text-gray-500">Nothing here. Enjoy the quiet.</p>
    </div>

    <!-- Search matched nothing (but the list itself isn't empty) -->
    <div v-else-if="searchQuery.trim() && !openItems.length && !completedItems.length && !isAdding" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <p class="text-sm text-gray-400 dark:text-gray-500">No tasks match "{{ searchQuery.trim() }}".</p>
    </div>

    <!-- Loaded: Desktop — flat list, dense rows -->
    <div v-else-if="!isMobile" class="flex-1 overflow-y-auto">
      <div class="relative">
        <!-- Inline add row -->
        <Transition name="task-row">
          <div v-if="isAdding" class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <span class="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-200 dark:border-gray-700"></span>
            <input
              ref="newTitleInput"
              v-model="newTitle"
              type="text"
              placeholder="Task title"
              class="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-100 bg-transparent border-0 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent"
              @keydown.enter="confirmAdd"
              @keydown.esc="cancelAdd"
              @blur="onBlur"
            />
          </div>
        </Transition>

        <!-- Today/This week: drag to reorder (local-only, see useChecklistOrder.js).
             Plain tag="div" — vuedraggable's tag="transition-group" mode
             crashes on mount in this Vue version ("Cannot set properties of
             null (setting '__draggable_context')", a known vuedraggable/Vue
             3.4+ incompatibility), so this list loses the fade-out-on-complete
             animation the non-reorderable chips below still have; SortableJS's
             own :animation still smoothly reflows siblings during an actual
             drag, which is the animation that actually matters here. -->
        <draggable
          v-if="reorderable"
          v-model="orderedOpenItems"
          tag="div"
          :item-key="rowKey"
          class="relative divide-y divide-gray-100 dark:divide-gray-800"
          :animation="220"
          handle=".task-row-handle"
          ghost-class="task-row-ghost"
          chosen-class="task-row-chosen"
          @end="persistOrder"
        >
          <template #item="{ element }">
            <TaskRow
              :task="element" :reorderable="true" :just-created="justCreated.has(rowKey(element))"
              @open="openTask" @toggle="toggle"
              @update-priority="updatePriority" @update-due-date="updateDueDate"
            />
          </template>
        </draggable>

        <!-- Overdue/Someday/All: no drag, keep the nicer TransitionGroup
             fade for completing/leaving rows. -->
        <TransitionGroup v-else tag="div" name="task-row" class="relative divide-y divide-gray-100 dark:divide-gray-800">
          <TaskRow
            v-for="t in openItems" :key="rowKey(t)" :task="t" :just-created="justCreated.has(rowKey(t))"
            @open="openTask" @toggle="toggle"
            @update-priority="updatePriority" @update-due-date="updateDueDate"
          />
        </TransitionGroup>
      </div>

      <div v-if="completedItems.length" class="mt-2">
        <button
          @click="showCompleted = !showCompleted"
          class="w-full flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide"
        >
          <svg class="w-3 h-3 transition-transform" :class="showCompleted ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          Completed ({{ completedItems.length }})
        </button>
        <TransitionGroup v-if="showCompleted" tag="div" name="task-row" class="relative divide-y divide-gray-100 dark:divide-gray-800">
          <TaskRow
            v-for="t in completedItems" :key="rowKey(t)" :task="t" :just-created="justCreated.has(rowKey(t))"
            @open="openTask" @toggle="toggle"
            @update-priority="updatePriority" @update-due-date="updateDueDate"
          />
        </TransitionGroup>
      </div>
    </div>

    <!-- Loaded: Mobile — grouped rounded card, section label, matches the
         app's standard mobile pattern (see CLAUDE.md "Mobile-specific UI
         patterns" — grouped cards, section labels above, no per-row borders). -->
    <div v-else class="flex-1 overflow-y-auto pb-4">
      <!-- Inline add row — its own floating card, not squeezed into the group -->
      <Transition name="task-row">
        <div v-if="isAdding" class="flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-[#1C2128] rounded-2xl mx-4 mt-4 mb-3 border border-blue-100 dark:border-blue-900/40">
          <span class="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-700"></span>
          <input
            ref="newTitleInput"
            v-model="newTitle"
            type="text"
            placeholder="Task title"
            class="flex-1 min-w-0 text-[15px] text-gray-800 dark:text-gray-100 bg-transparent border-0 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent"
            @keydown.enter="confirmAdd"
            @keydown.esc="cancelAdd"
            @blur="onBlur"
          />
        </div>
      </Transition>

      <p class="px-4 pt-4 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{{ scopeLabel }}</p>
      <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
        <draggable
          v-if="reorderable"
          v-model="orderedOpenItems"
          tag="div"
          :item-key="rowKey"
          :animation="220"
          handle=".task-row-handle"
          ghost-class="task-row-ghost"
          chosen-class="task-row-chosen"
          @end="persistOrder"
        >
          <template #item="{ element }">
            <TaskRow
              :task="element" :reorderable="true" :just-created="justCreated.has(rowKey(element))"
              @open="openTask" @toggle="toggle"
              @update-priority="updatePriority" @update-due-date="updateDueDate"
            />
          </template>
        </draggable>
        <TransitionGroup v-else tag="div" name="task-row">
          <TaskRow
            v-for="t in openItems" :key="rowKey(t)" :task="t" :just-created="justCreated.has(rowKey(t))"
            @open="openTask" @toggle="toggle"
            @update-priority="updatePriority" @update-due-date="updateDueDate"
          />
        </TransitionGroup>
      </div>

      <div v-if="completedItems.length" class="mt-5">
        <button
          @click="showCompleted = !showCompleted"
          class="w-full flex items-center gap-1.5 px-4 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
        >
          <svg class="w-3 h-3 transition-transform" :class="showCompleted ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          Completed ({{ completedItems.length }})
        </button>
        <div v-if="showCompleted" class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <TransitionGroup tag="div" name="task-row">
            <TaskRow
              v-for="t in completedItems" :key="rowKey(t)" :task="t" :just-created="justCreated.has(rowKey(t))"
              @open="openTask" @toggle="toggle"
              @update-priority="updatePriority" @update-due-date="updateDueDate"
            />
          </TransitionGroup>
        </div>
      </div>
    </div>

    <FlashNotifications ref="flash" />
    <TaskCompleteConfetti ref="confettiRef" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import draggable from 'vuedraggable'
import { useTasksStore } from 'stores/tasks.js'
import { useTaskDates } from 'composables/useTaskDates.js'
import { useChecklistOrder } from 'composables/useChecklistOrder.js'
import { getCachedChecklistCount } from 'composables/useTasksChecklistCountCache.js'
import { TASK_COLOR_HEX } from './taskColors.js'
import TaskRow from './TaskRow.vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import TaskCompleteConfetti from './TaskCompleteConfetti.vue'

const props = defineProps({
  // TasksPage.vue mounts a desktop instance and a mobile instance side by
  // side (each shown/hidden purely via responsive CSS on their wrapper —
  // see TasksPage.vue's own comment on why). This prop tells each instance
  // which markup branch to render, so only one branch actually mounts per
  // instance instead of doubling up dead, CSS-hidden subtrees (duplicate
  // <draggable>/<TransitionGroup> instances that would never be visible).
  variant: { type: String, default: 'desktop' },
})
const isMobile = computed(() => props.variant === 'mobile')

const router = useRouter()
const { todayStrInUserTz } = useTaskDates()
const { applyOrder, saveOrder, isReorderable } = useChecklistOrder()
const flash = ref(null)
const confettiRef = ref(null)

// See docs/skeleton_count_caching.md — a computed (not a ref read once) so
// switching chips picks up that chip's own cached count immediately,
// synchronously, before the new scope's fetch even resolves.
const skeletonCount = computed(() => Math.min(getCachedChecklistCount(store.checklistScope), 20))

const CHIPS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'someday', label: 'Someday' },
  { value: 'all', label: 'All' },
]

const store = useTasksStore()
const showCompleted = ref(false)

// Client-side title search — scoped within whichever chip is already
// active, not a separate fetch. Persists across chip switches on purpose
// (searching, then changing scope, keeps refining rather than resetting).
const searchQuery = ref('')
function matchesSearch(task) {
  const q = searchQuery.value.trim().toLowerCase()
  return !q || task.title.toLowerCase().includes(q)
}

const openItems = computed(() => store.checklistItems.filter(t => t.status !== 'completed' && matchesSearch(t)))
const completedItems = computed(() => store.checklistItems.filter(t => t.status === 'completed' && matchesSearch(t)))

// Open-task count beside each chip's label, so the other four scopes are
// legible without clicking through them — the board has had per-column counts
// all along and the checklist was the odd one out.
//
// Completed tasks are excluded (backend-side, see #checklist_counts): the
// list already reports those separately under its own "Completed (n)" header,
// and a chip still reading 5 after all five are ticked off would be pointing
// at work that no longer needs doing.
//
// Zero renders as nothing rather than "0". An empty Overdue chip saying 0 is
// noise on a bar of five chips, and its absence already reads as "none".
// Undefined (counts not fetched yet) falls through the same branch, so chips
// never flash a placeholder 0 on first paint.
function chipCount(scope) {
  return store.checklistCounts[scope] || 0
}

// Muted against either chip background — the label is the thing being read,
// the number is a footnote to it.
function countClass(scope) {
  return store.checklistScope === scope ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'
}

// Section label above the mobile grouped card — reuses the same chip labels
// shown in the scope pills above, so "This week" pills into "This week" here.
const scopeLabel = computed(() => CHIPS.find(c => c.value === store.checklistScope)?.label || 'Tasks')

// Local-only manual ordering, "Today"/"This week" only — see
// useChecklistOrder.js for why (rolling time windows, per-task expiry).
// vuedraggable needs a genuine settable array for its v-model, which
// openItems (a computed filter over store state) can't be directly — this
// mirrors it, re-derived (custom order re-applied) whenever the underlying
// scope-filtered list changes, e.g. after a fetch, a checkbox toggle, or a
// due-date edit bumping a task out of scope.
const reorderable = computed(() => isReorderable(store.checklistScope))
const orderedOpenItems = ref([])

watch(openItems, (items) => {
  orderedOpenItems.value = applyOrder(store.checklistScope, items)
}, { immediate: true })

// Drag finished — capture the *entire* current visual order (not just the
// dragged item's new slot) as the new custom order, so tasks that had no
// previous position also get one going forward.
function persistOrder() {
  if (!reorderable.value) return
  saveOrder(store.checklistScope, orderedOpenItems.value.map(t => t.id))
}

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}

// Optimistic: store.setStatus flips the row's status (and thus which list
// it's in — see openItems/completedItems above) immediately, before the
// PATCH resolves, so the row animates into place right away. If the request
// fails, the store reverts the status itself; this just tells the user why
// it snapped back.
//
// `event` is the checkbox's raw click MouseEvent (TaskRow.vue passes it
// through on the 'toggle' emit) — used to anchor the completion confetti at
// the click position. This burst deliberately fires from here, before
// store.setStatus even runs, not from a watcher inside TaskRow itself: the
// completing row is removed from openItems in the same reactive tick its
// status flips, so a per-row watcher would have its component torn down
// before — or mid — its own animation. Firing here, into a canvas that
// lives at this component's level (TaskCompleteConfetti.vue), sidesteps
// that entirely.
async function toggle(task, event) {
  const nextStatus = task.status === 'completed' ? 'pending' : 'completed'
  if (nextStatus === 'completed' && event) {
    confettiRef.value?.burst(event.clientX, event.clientY, task.color_css_code)
  }

  const updated = await store.setStatus(task, nextStatus)
  if (!updated) {
    flash.value?.show("Couldn't update the task — please try again.", 'alert')
    return
  }
  // Ticking a row off doesn't change which scope it belongs to, so there's
  // nothing here for the optimistic patch to re-bucket and no reason to
  // refetch the list — but the chip counts exclude completed tasks, so they
  // are now wrong by one. This refreshes just the numbers, which is why
  // #checklist_counts is its own endpoint rather than a key on the list
  // payload. Not awaited: the row has already animated, and the count
  // settling a moment later is fine.
  store.fetchChecklistCounts()
}

// Same optimistic shape as toggle() above. Priority never changes which
// scope bucket a task belongs to, so patchLocal alone (inside
// store.quickUpdate) is enough — no refetch needed.
async function updatePriority(task, priority) {
  const updated = await store.quickUpdate(task, { priority })
  if (!updated) {
    flash.value?.show("Couldn't update the priority — please try again.", 'alert')
  }
}

// A due-date edit can move a task out of (or into) the currently active
// scope filter — e.g. picking "Tomorrow" while viewing "Today" — which
// store.quickUpdate's in-place patch can't detect on its own. Re-fetch the
// current scope on success so the row leaves cleanly; the checklist's own
// <TransitionGroup> (see openItems/completedItems rendering) animates it
// out the same way completing a task does.
async function updateDueDate(task, dueAt) {
  const updated = await store.quickUpdate(task, { due_at: dueAt })
  if (!updated) {
    flash.value?.show("Couldn't update the due date — please try again.", 'alert')
    return
  }
  await store.fetchChecklist(store.checklistScope)
}

function retry() {
  store.invalidate()
  store.fetchChecklist(store.checklistScope)
}

// ── Inline "New task" row — no navigation to /tasks/new, matches the
// board's per-column quick add ────────────────────────────────────────────
const isAdding = ref(false)
const newTitle = ref('')
const newTitleInput = ref(null)

async function startAdd() {
  isAdding.value = true
  newTitle.value = ''
  await nextTick()
  newTitleInput.value?.focus()
}

function cancelAdd() {
  isAdding.value = false
  newTitle.value = ''
}

// A quick-added task needs *some* due date to actually show up again after
// refetch under the currently active filter chip — "someday" is the one
// scope that means "no due date" on purpose, so leave it unset there.
// `todayStrInUserTz()` (not a raw `new Date()`) so "today" here means today
// in the account's configured timezone, same as the checklist's own "Today"
// chip filter and TaskDetailPage's due-date field — see splitDateTime's
// comment in TaskDetailPage.vue for the bug this class of mistake caused.
function defaultDueAtForScope() {
  if (store.checklistScope === 'someday') return null
  return `${todayStrInUserTz()}T23:59:00`
}

// The row TaskRow.vue renders while the POST is still in flight. Every field
// the row template touches has to be present and of the right type — it binds
// them unguarded, so a half-built object renders as a visibly broken row for
// the length of the round trip. The values mirror the columns' database
// defaults (see the schema annotation on app/models/task.rb), which is what
// the server is about to echo back for a task created with only a title.
//
// `_key` is the row's identity for Vue, deliberately separate from `id`: the
// store carries it onto the server's record so replacing the temporary id
// doesn't remount the row.
let optimisticSeq = 0

function buildOptimisticTask(title, dueAt) {
  const key = `new-${Date.now()}-${optimisticSeq++}`
  return {
    id: key,
    _key: key,
    title,
    description: '',
    status: 'pending',
    priority: 'medium',
    color: 'peacock',
    color_css_code: TASK_COLOR_HEX.peacock,
    due_at: dueAt,
    remind_at: null,
    position: 0,
    is_recurring: false,
    is_overdue: false,
    carry_over: false,
    shared_publicly: false,
    parent_task_id: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
}

// Rows are keyed on `_key` when they have one, so an optimistic row keeps the
// same DOM node when its temporary id is swapped for the server's real one.
function rowKey(task) {
  return task._key || task.id
}

// Which rows are currently playing their creation animation, by row key.
//
// Held here rather than as a flag on the task itself, for two reasons. The
// store replaces the placeholder object wholesale with the server's record, so
// a flag on the placeholder would blink out the moment the response landed,
// cutting the animation short. And it is purely a view state — parking it on
// cached task data would leave it to ride along and replay on some unrelated
// later render. Keyed on `_key`, which is precisely the thing that survives
// the swap.
const CREATED_ANIMATION_MS = 620
const justCreated = ref(new Set())

function markJustCreated(key) {
  justCreated.value.add(key)
  setTimeout(() => justCreated.value.delete(key), CREATED_ANIMATION_MS)
}

// The row lands in the list first and the request follows it, rather than the
// other way round — typing a title and pressing Enter now costs nothing, and
// several can be queued in a row without waiting on any of them. A rejected
// create takes its row back out (store.createTaskOptimistic) and says so.
async function confirmAdd() {
  const title = newTitle.value.trim()
  if (!title) { cancelAdd(); return }

  const dueAt = defaultDueAtForScope()
  const placeholder = buildOptimisticTask(title, dueAt)

  // Cleared synchronously, before anything is awaited: the input is ready for
  // the next title in the same tick the row appears. No `addSaving` guard any
  // more — concurrent creates are the point, and each one owns a distinct
  // placeholder, so they cannot tread on each other.
  newTitle.value = ''
  newTitleInput.value?.focus()

  markJustCreated(placeholder._key)

  const created = await store.createTaskOptimistic(placeholder, { title, due_at: dueAt })
  if (!created) {
    flash.value?.show(`Couldn't create "${title}" — it has been removed from the list.`, 'alert')
  }
}

// Clicking/tabbing away with nothing typed closes the row. Enter doesn't
// blur the input (there's no surrounding <form> to trigger a submit-blur),
// so this never fights with confirmAdd's own refocus.
function onBlur() {
  if (!newTitle.value.trim()) cancelAdd()
}

defineExpose({ startAdd })

onMounted(() => store.fetchChecklistIfStale())
</script>

<style scoped>
/* FLIP-style reflow for rows entering/leaving openItems/completedItems (a
   status toggle moves a row between the two lists — see the toggle()
   comment above). Leaving rows go absolute so the rows below them can slide
   up into place immediately instead of waiting for the leaving row's fade
   to finish first. */
.task-row-move,
.task-row-enter-active,
.task-row-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
}
.task-row-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.task-row-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
.task-row-leave-active {
  position: absolute;
  width: 100%;
}

/* Drag state for the Today/This week reorder handle (see the <draggable>
   in the template) — ghost is the gap left at the drop target, chosen is
   the row actually being dragged.

   Each selector is compounded with `.task-row` on purpose. TaskRow.vue paints
   every row with a wash of its own color (`.task-row { background-color:
   var(--task-tint) }`), and in a scoped stylesheet that is
   `.task-row[data-v-child]` — exactly as specific as a bare
   `:deep(.task-row-ghost)` here, which compiles to
   `[data-v-parent] .task-row-ghost`. A tie between two components'
   stylesheets is settled by whichever happens to be injected last, so without
   the extra class these drag states would win or lose on bundler ordering
   alone. Adding `.task-row` makes them strictly more specific, and therefore
   stable. */
:deep(.task-row.task-row-ghost) {
  opacity: 0.5;
  background: rgb(239 246 255);
}
:global(.dark) :deep(.task-row.task-row-ghost) {
  background: rgb(30 58 95 / 0.35);
}
:deep(.task-row.task-row-chosen) {
  background: rgb(255 255 255);
  box-shadow: 0 8px 20px -6px rgb(0 0 0 / 0.15);
  border-radius: 0.5rem;
  cursor: grabbing;
}
:global(.dark) :deep(.task-row.task-row-chosen) {
  background: rgb(28 33 40);
}

.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>
